import React, { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../common/Button';
import { supabase } from '../../config/supabase';
import { useAuthStore } from '../../store/authStore';
import imageCompression from 'browser-image-compression';

const UploadReadingPhoto: React.FC = () => {
  const { readingId } = useParams<{ readingId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Validate readingId
  if (!readingId) {
    navigate('/history');
    return null;
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset states
    setError(null);
    setUploadProgress(0);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Create preview
    const preview = URL.createObjectURL(file);
    setPreviewUrl(preview);
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !user || !readingId) return;

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // First verify the reading exists and belongs to the user
      const { data: reading, error: readingError } = await supabase
        .from('readings')
        .select('id')
        .eq('id', readingId)
        .eq('user_id', user.id)
        .single();

      if (readingError || !reading) {
        throw new Error('Reading not found or access denied');
      }

      // Start progress animation
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) return prev; // Cap at 90% until complete
          return prev + 1;
        });
      }, 100);

      // Compress image if needed
      let imageFile = selectedFile;
      if (selectedFile.size > 2 * 1024 * 1024) { // Over 2MB
        const options = {
          maxSizeMB: 2,
          maxWidthOrHeight: 1600,
          useWebWorker: true
        };

        imageFile = await imageCompression(selectedFile, options);
      }

      // Create unique filename
      const fileExt = selectedFile.name.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = `${user.id}/${readingId}.${fileExt}`;
      const filePath = `reading-images/${fileName}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('tarot-images')
        .upload(filePath, imageFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase.storage
        .from('tarot-images')
        .getPublicUrl(filePath);

      if (!data?.publicUrl) {
        throw new Error('Failed to get public URL');
      }

      // Update reading with image URL
      const { error: updateError } = await supabase
        .from('readings')
        .update({ image_url: data.publicUrl })
        .eq('id', readingId)
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      // Complete progress
      clearInterval(progressInterval);
      setUploadProgress(100);

      // Navigate to next steps after a short delay
      setTimeout(() => {
        navigate('/next-steps');
      }, 500);

    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload image');
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSkip = () => {
    navigate('/next-steps');
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] bg-parchment dark:bg-ink">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-3xl mx-auto"
      >
        <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6 mb-6">
          <h1 className="text-3xl md:text-4xl font-display font-semibold text-ink dark:text-parchment">
            Add a Photo to Your Reading
          </h1>
          <p className="text-ink dark:text-parchment/60 mt-2">
            Would you like to upload a picture of your tarot card spread to your saved reading?
          </p>
        </div>

        <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl p-6 space-y-6">
          {error && (
            <div className="bg-sun/10 text-sun p-4 rounded-md">
              {error}
            </div>
          )}

          {!selectedFile ? (
            <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-mist dark:border-dusk rounded-lg">
              <span className="text-4xl text-mist mb-4">↑</span>
              <p className="text-ink/70 dark:text-parchment/60 mb-4 text-center">
                Drag and drop your image here, or click to select
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose File
              </Button>
            </div>
          ) : (
            <div className="relative">
              <div className="aspect-video relative rounded-lg overflow-hidden bg-vellum dark:bg-shadow">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Selected"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <span className="text-4xl text-mist">✧</span>
                  </div>
                )}
              </div>
              <button
                onClick={clearSelection}
                className="absolute top-2 right-2 p-1 bg-shadow/50 hover:bg-shadow/75 rounded-full text-white transition-colors"
              >
                <span className="text-lg leading-none">×</span>
              </button>
            </div>
          )}

          {isUploading && (
            <div className="w-full">
              <div className="h-2 bg-mist dark:bg-dusk rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-sun to-parchment0"
                  initial={{ width: "0%" }}
                  animate={{ width: `${uploadProgress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              <p className="text-sm text-center text-ink/70 dark:text-parchment/60 mt-2">
                Uploading your photo...
              </p>
            </div>
          )}

          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={handleSkip}
              disabled={isUploading}
            >
              Skip for Now
            </Button>
            {selectedFile && (
              <Button
                variant="primary"
                onClick={handleUpload}
                isLoading={isUploading}
                disabled={isUploading}
              >
                Upload Photo
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default UploadReadingPhoto;
